"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser";

/**
 * Invisible client component that listens for Supabase auth state
 * changes (SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED) and triggers a
 * Next.js router.refresh() so server components re-render with the current
 * session. It also notifies the client chrome shell so the nav can refetch
 * user-specific state without making the root layout dynamic.
 *
 * Fixes: user logs in via magic link in another tab, but the
 * original tab's nav still shows "Log In / Sign Up" until manual
 * page refresh.
 */
export function AuthRefresh() {
  const router = useRouter();
  const lastEvent = useRef<string | null>(null);

  useEffect(() => {
    const supabase = getSupabaseBrowserClient();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Only refresh on actual sign-in/out transitions, not on every
      // token refresh (which fires frequently and would cause flicker).
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        // Dedupe: don't refresh if we just handled the same event
        if (lastEvent.current === event) return;
        lastEvent.current = event;
        window.dispatchEvent(new Event("wildroost-auth-change"));
        router.refresh();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router]);

  return null;
}
